import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl } from '../../permissions/access';
import { useEffect, useState } from "react";
import { Pagination, Button, Space, Descriptions, Popconfirm, Table, Typography, Tag, Input, Select, DatePicker } from "antd";
import dayjs from "dayjs";
import { deleteHotVideo, fetchHotVideos } from "../../api/discover";
import UserInfoView from "../../components/community/user_info_view";
import DiscoverHotPostEditDrawer from "../../components/discover/hot_post_edit_view";
import DiscoverPreview from "../../components/discover/discover_preview";
import { useDebouncedCallback } from "use-debounce";

const DiscoverHotVideosScreen = () => {
    const [posts, setPosts] = useState([]);
    const [open, setOpen] = useState(false);
    const [currPost, setCurrPost] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [searchParams, setSearchParams] = useState({
        title: '',
        status: '',
        start: '',
        end: '',
    });
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const tabCols = [
        {
            title: 'VideoId',
            dataIndex: 'id',
            key: 'id',
            render: (_, record) => {
                const { post } = record;
                return <div style={{ width: 80 }}><Typography.Text copyable style={{ fontSize: 11, color: '#afafaf' }}>{post.id}</Typography.Text></div>
            },
        },
        {
            title: 'Author',
            dataIndex: 'user',
            key: 'user',
            render: (_, record) => {
                const { post } = record;
                return <UserInfoView user={post.user} />
            },
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (_, record) => {
                const { post } = record;
                return <b>{post.title}</b>
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (_, record) => {
                const { post } = record;
                return <label style={{ fontSize: 12, color: '#afafaf', whiteSpace: 'nowrap' }}>{dayjs(post.createdAt).format('MM-DD HH:mm')}</label>
            },
        },
        {
            title: 'Weight',
            dataIndex: 'weight',
            key: 'weight',
            render: (text, _) => {
                if (!text) return '-';
                return <b>{text}</b>
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text, _) => {
                if (String(text).toLowerCase() === 'active') {
                    return <Tag color="green">{text}</Tag>
                }
                return <Tag color="red">{text}</Tag>
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (text, record) => {
                return (
                    <Button.Group size='small'>
                        <PermissionControl permission="hotvideo.control.reviewHandle.bfb94725"><Button type='link' onClick={() => { reviewHandle(record) }}>Review Video</Button></PermissionControl>
                        <PermissionControl permission="hotvideo.control.editHandle.08a2ceb3"><Button type='link' onClick={() => { editHandle(record) }}>Edit</Button></PermissionControl>
                        <PermissionControl permission="hotvideo.control.deleteHandle.3c7e7490"><Popconfirm
                            title="Delete Action"
                            description="Are you sure to remove this hot video for discover?"
                            onConfirm={() => { deleteHandle(record) }}
                            okText="Yes"
                            cancelText="No" >
                            <Button danger type='link'>delete</Button>
                        </Popconfirm></PermissionControl>
                    </Button.Group>
                )
            },
        }
    ];

    useEffect(() => {
        getData(pageNum, pageSize, searchParams);
    }, [])

    const getData = (pNum, pSize, search) => {
        setLoading(true);
        let params = {
            'page.num': pNum,
            'page.size': pSize,
            ...search
        }
        fetchHotVideos(params).then(res => {
            if (res.list) {
                setPosts(res.list);
            }
            if (res.page) {
                setTotal(res.page.total);
            }
            setLoading(false);
        }).catch(showRequestError).finally(() => { setLoading(false); })
    }

    const editHandle = (record) => {
        setCurrPost(record);
        setOpen(true);
    }

    const deleteHandle = (record) => {
        setLoading(true);
        deleteHotVideo(record.id).then(res => {
            getData(pageNum, pageSize, searchParams);
        }).finally(() => {
            setLoading(false)
        }).catch(showRequestError)
    }

    const reviewHandle = (record) => {
        setCurrPost(record);
        setReviewOpen(true);
    }

    const onSearchChange = useDebouncedCallback(
        (tag, value) => {
            let newParams = { ...searchParams };
            if (tag === 'dates') {
                if (value.length == 2) {
                    newParams.start = `${value[0]}T00:00:00Z`;
                    newParams.end = `${value[1]}T23:59:59Z`;
                } else {
                    newParams.start = '';
                    newParams.end = '';
                }
            } else {
                newParams[tag] = value;
            }
            setSearchParams(newParams);
            setPageNum(1);
            getData(1, pageSize, newParams);
        },
        300
    )

    const getTitleView = () => {
        return (
            <Space>
                <Input
                    placeholder="keywords search"
                    allowClear
                    onChange={evt => onSearchChange('title', evt.target.value)}
                />
                <Select
                    style={{ width: 160 }}
                    options={[
                        { key: 1, label: 'Active', value: 0 },
                        { key: 2, label: 'Inactive', value: 1 },
                    ]}
                    placeholder='status'
                    onChange={value => onSearchChange('status', value)}
                    allowClear
                />
                <DatePicker.RangePicker onChange={values => onSearchChange('dates', (values ?? []).map(date => dayjs(date).format('YYYY-MM-DD')))} />
            </Space>
        )
    }

    const getExtraView = () => {
        return (
            <Space>
                <PermissionControl permission="hotvideo.control.editHandle.657e2c54"><Button type="primary" onClick={() => editHandle(null)}>Add Hot Video</Button></PermissionControl>
            </Space>
        )
    }

    const pageChange = (page, size) => {
        setPageNum(page);
        setPageSize(size);
        getData(page, size, searchParams);
    }

    return (
        <div style={{ padding: 20 }}>
            <Descriptions title={getExtraView()} extra={getTitleView()} column={3}>
                <Descriptions.Item span={3}>
                    <Table
                        style={{ width: '100%' }}
                        loading={loading}
                        rowKey={(record) => record.post.id}
                        rowClassName={'table-row'}
                        size='small'
                        dataSource={posts}
                        columns={tabCols}
                        pagination={false} />
                </Descriptions.Item>
            </Descriptions>
            <div className='footer-part cnt-item'>
                <Pagination
                    total={total}
                    showTotal={(total) => `total ${total}`}
                    current={pageNum}
                    pageSize={pageSize}
                    showSizeChanger={true}
                    pageSizeOptions={[10, 20, 30, 50]}
                    onChange={(page, size) => pageChange(page, size)}
                />
            </div>
            <DiscoverHotPostEditDrawer
                isVideo={true}
                open={open}
                defaultPost={currPost}
                onClose={() => setOpen(false)}
                onUpdate={() => getData(pageNum, pageSize, searchParams)} />

            <DiscoverPreview
                open={reviewOpen}
                discover={currPost?.post}
                onClose={() => setReviewOpen(false)}
            />
        </div>
    );
}
export default DiscoverHotVideosScreen;